import React, { useRef, useEffect, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

import { Icon, IconBox } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Skeleton from "@/ui/atoms/Skeleton";
import TextField from "@/ui/atoms/TextField";

type GoogleMapsPlacePickerProps = {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
};

export const GoogleMapsPlacePickerSkeletonLoader = () => (
    <Skeleton variant="text" sx={{ height: "32px" }} />
);

export const GoogleMapsPlacePicker = ({
    onPlaceSelect,
}: GoogleMapsPlacePickerProps) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const [isAutocompleteReady, setIsAutocompleteReady] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const readyCheckIntervalRef = useRef<number | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary("places");

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options = {
            fields: [
                "geometry",
                "name",
                "formatted_address",
                "address_components",
            ],
        };

        const autocomplete = new places.Autocomplete(inputRef.current, options);
        setPlaceAutocomplete(autocomplete);

        const checkAutocompleteReady = () => {
            try {
                const pacContainer = document.querySelector(".pac-container");
                const hasAutocompleteUI = !!pacContainer;

                if (hasAutocompleteUI) {
                    setIsAutocompleteReady(true);
                    if (readyCheckIntervalRef.current !== null) {
                        window.clearInterval(readyCheckIntervalRef.current);
                    }
                }
            } catch {
                // Continue checking if there's an error
            }
        };

        readyCheckIntervalRef.current = window.setInterval(
            checkAutocompleteReady,
            100,
        );

        return () => {
            if (readyCheckIntervalRef.current !== null) {
                window.clearInterval(readyCheckIntervalRef.current);
            }
        };
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener("place_changed", () => {
            onPlaceSelect(placeAutocomplete.getPlace());
        });
    }, [onPlaceSelect, placeAutocomplete]);

    const handleClear = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.focus();
        }
    };

    const trackInputFocus = React.useCallback(() => {
        // @HACK: using `onFocus` and `onBlur` from TextField component ends with overwriting the internal component events
        const el = inputRef.current;
        if (!el) return;

        const handleFocus = () => setIsFocused(true);
        const handleBlur = () => setIsFocused(false);

        el.addEventListener("focus", handleFocus);
        el.addEventListener("blur", handleBlur);

        return () => {
            el.removeEventListener("focus", handleFocus);
            el.removeEventListener("blur", handleBlur);
        };
    }, []);
    useEffect(trackInputFocus, [trackInputFocus]);

    return (
        <Box sx={{ position: "relative", width: "100%", minHeight: "39px" }}>
            {!isAutocompleteReady && (
                <Box
                    sx={{
                        position: "absolute",
                        width: "100%",
                        background: "white",
                        zIndex: 1,
                    }}
                >
                    <GoogleMapsPlacePickerSkeletonLoader />
                </Box>
            )}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 0,
                    width: "100%",
                    "& .pac-container": { zIndex: "10000 !important" },
                }}
            >
                <TextField
                    name="place"
                    leadingIcon={"magnifying-glass"}
                    ref={inputRef}
                    disabled={!isAutocompleteReady}
                    placeholder="Start typing property name or address"
                />
                <Box
                    component="button"
                    onClick={handleClear}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                        right: "0.5rem",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                    }}
                    aria-label="Clear"
                >
                    <IconBox>
                        <Icon
                            icon="xmark"
                            color={
                                isFocused
                                    ? "var(--proda-black)"
                                    : "var(--proda-black-10)"
                            }
                        />
                    </IconBox>
                </Box>
            </Box>
        </Box>
    );
};

export { APIProvider as GoogleMapsAPIProvider } from "@vis.gl/react-google-maps";
